import type {BymoPageContextProps} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import type {DoppeViewerPagesRoutesManager} from '../viewer-pages-routes-manager/doppe-viewer-pages-routes-manager';
import {DoppeDtoStoreProductType} from '../types/doppe-dto-store-product';
import {DoppeDtoBookingServiceType} from '../types/doppe-dto-booking-service';
import {DoppeDtoEventRegistrationType} from '../types/doppe-dto-event';
import {
    doppeHtmlSiteResolveProductPageUrl,
    doppeHtmlSiteResolveServicePageUrl,
    doppeHtmlSiteResolveBlogPostPageUrl,
    doppeHtmlSiteResolveEventPageUrl,
    doppeHtmlSiteResolveAllProductsPageUrl,
    doppeHtmlSiteResolveBookOnlinePageUrl,
    doppeHtmlSiteResolveAllPostsPageUrl,
    doppeHtmlSiteResolveEventsPageURL,
    doppeHtmlSiteResolveBookingFormUrl,
    doppeHtmlSiteResolveEventRsvpFormUrl,
} from '../types/doppe-html-site';
import type {DoppeViewerLang} from '../doppe-viewer/doppe-viewer-lang';

/**
 * Entity info required for navigation - contains slug and page URL for fallback
 */
export interface VerticalEntityInfo {
    /** The slug used for Hopp internal routing */
    slug?: string | null;
    /** The full URL to the entity page on Wix site (used when useHoppVerticalItemPages is false) */
    pageUrl?: string | null;
}

/**
 * Options for vertical entity navigation
 */
export interface VerticalEntityNavigationOptions {
    /** Context props containing the useHoppVerticalItemPages flag and htmlSiteInfo */
    bymoPageContextProps: BymoPageContextProps;
    /** The viewer language */
    viewerLang: DoppeViewerLang;
    /** Whether to show mobile view (for htmlSiteInfo URLs) */
    showMobileView: boolean;
    /** When provided, internalPagePath will be computed in the result */
    routesManager?: DoppeViewerPagesRoutesManager;
}

/**
 * Result of determining navigation target for a vertical entity
 */
export interface VerticalEntityNavigationResult {
    /** Whether to use Hopp's internal routing */
    useInternalRouting: boolean;
    /** The slug to use for internal routing (only if useInternalRouting is true) */
    slug?: string;
    /** The external URL to open (only if useInternalRouting is false) */
    externalUrl?: string | null;
    /** The internal page path (e.g. /product/:slug). Only present when useInternalRouting is true and routesManager was provided in options. */
    internalPagePath?: string;
}

/**
 * Determines the navigation target for a product entity.
 * 
 * When disableHoppVerticalProductPages is false/unset: Returns internal routing with product slug
 * When disableHoppVerticalProductPages is true: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveProductTarget(
    entityInfo: VerticalEntityInfo & { productPageUrl?: string | null; productType?: DoppeDtoStoreProductType },
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView, routesManager} = options;
    const disableHoppVerticalProductPages = bymoPageContextProps.disableHoppVerticalProductPages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (!disableHoppVerticalProductPages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug,
            internalPagePath: routesManager
                ? viewerEntityNavigationResolveProductInternalPagePath(routesManager, entityInfo.slug, entityInfo.productType)
                : undefined
        };
    }

    // Fall back to external URL
    const productPageUrl = entityInfo.productPageUrl ?? entityInfo.pageUrl;
    if (htmlSiteInfo && productPageUrl) {
        const externalUrl = doppeHtmlSiteResolveProductPageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            productPageUrl: productPageUrl,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for a service entity.
 *
 * When disableHoppVerticalServicePages is false/unset: Returns internal routing with service slug
 * When disableHoppVerticalServicePages is true: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveServiceTarget(
    entityInfo: VerticalEntityInfo & { servicePageUrl?: string | null; serviceType?: DoppeDtoBookingServiceType },
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView, routesManager} = options;
    const disableHoppVerticalServicePages = bymoPageContextProps.disableHoppVerticalServicePages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (!disableHoppVerticalServicePages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug,
            internalPagePath: routesManager
                ? viewerEntityNavigationResolveServiceInternalPagePath(routesManager, entityInfo.slug, entityInfo.serviceType)
                : undefined
        };
    }

    // Fall back to external URL
    const servicePageUrl = entityInfo.servicePageUrl ?? entityInfo.pageUrl;
    if (htmlSiteInfo && servicePageUrl) {
        const externalUrl = doppeHtmlSiteResolveServicePageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            pageURL: servicePageUrl,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for a blog post entity.
 * 
 * When useHoppVerticalBlogPostPages is true: Returns internal routing with post slug
 * When useHoppVerticalBlogPostPages is false: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveBlogPostTarget(
    entityInfo: VerticalEntityInfo,
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView, routesManager} = options;
    const useHoppVerticalBlogPostPages = bymoPageContextProps.useHoppVerticalBlogPostPages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalBlogPostPages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug,
            internalPagePath: routesManager
                ? viewerEntityNavigationResolveBlogPostInternalPagePath(routesManager, entityInfo.slug)
                : undefined
        };
    }

    // Fall back to external URL using slug
    if (htmlSiteInfo && entityInfo.slug) {
        const externalUrl = doppeHtmlSiteResolveBlogPostPageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            slug: entityInfo.slug,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for an event entity.
 * 
 * When useHoppVerticalEventPages is true: Returns internal routing with event slug
 * When useHoppVerticalEventPages is false: Returns external URL from htmlSiteInfo
 */
export function viewerEntityNavigationResolveEventTarget(
    entityInfo: VerticalEntityInfo & { registrationType?: DoppeDtoEventRegistrationType },
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView, routesManager} = options;
    const useHoppVerticalEventPages = bymoPageContextProps.useHoppVerticalEventPages ?? false;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalEventPages && entityInfo.slug) {
        return {
            useInternalRouting: true,
            slug: entityInfo.slug,
            internalPagePath: routesManager
                ? viewerEntityNavigationResolveEventInternalPagePath(routesManager, entityInfo.slug, entityInfo.registrationType)
                : undefined
        };
    }

    // Fall back to external URL using slug
    if (htmlSiteInfo && entityInfo.slug) {
        const externalUrl = doppeHtmlSiteResolveEventPageUrl({
            doppeHtmlSiteInfo: htmlSiteInfo,
            slug: entityInfo.slug,
            viewerLang: viewerLang,
            showMobileView: showMobileView
        });

        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Opens an external URL based on the viewer mode.
 * 
 * In live preview mode: Opens the URL in a modal using the provided callback
 * In live viewer mode: Navigates to the URL in the same tab
 */
export function viewerEntityNavigationOpenExternalUrl(
    url: string,
    options: {
        isLivePreviewMode: boolean;
        openInModal: (url: string) => void;
    }
): void {
    if (options.isLivePreviewMode) {
        options.openInModal(url);
    } else {
        window.location.href = url;
    }
}

// ============================================================================
// List Page Navigation
// ============================================================================

/**
 * Options for vertical list page navigation
 */
export interface VerticalListNavigationOptions {
    /** Context props containing the useHoppVerticalListPage flags and htmlSiteInfo */
    bymoPageContextProps: BymoPageContextProps;
    /** The viewer language */
    viewerLang: DoppeViewerLang;
    /** Whether to show mobile view (for htmlSiteInfo URLs) */
    showMobileView: boolean;
}

/**
 * Result of determining navigation target for a vertical list page
 */
export interface VerticalListNavigationResult {
    /** Whether to use Hopp's internal routing */
    useInternalRouting: boolean;
    /** The external URL to open (only if useInternalRouting is false) */
    externalUrl?: string | null;
}

/**
 * Determines the navigation target for the Products LIST page.
 * 
 * When useHoppVerticalProductListPage is true: Returns internal routing to products list
 * When useHoppVerticalProductListPage is false: Returns external URL to HTML site Store page
 */
export function viewerListNavigationResolveProductsListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalProductListPage = true;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalProductListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveAllProductsPageUrl(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for the Services LIST page.
 * 
 * When useHoppVerticalServiceListPage is true: Returns internal routing to services list
 * When useHoppVerticalServiceListPage is false: Returns external URL to HTML site Booking Services page
 */
export function viewerListNavigationResolveServicesListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalServiceListPage = true;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalServiceListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveBookOnlinePageUrl(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for the Blog Posts LIST page.
 * 
 * When useHoppVerticalBlogPostListPage is true: Returns internal routing to blog posts list
 * When useHoppVerticalBlogPostListPage is false: Returns external URL to HTML site Blog page
 */
export function viewerListNavigationResolveBlogPostsListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalBlogPostListPage = true;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalBlogPostListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveAllPostsPageUrl(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

/**
 * Determines the navigation target for the Events LIST page.
 * 
 * When useHoppVerticalEventListPage is true: Returns internal routing to events list
 * When useHoppVerticalEventListPage is false: Returns external URL to HTML site Events page
 */
export function viewerListNavigationResolveEventsListTarget(
    options: VerticalListNavigationOptions
): VerticalListNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const useHoppVerticalEventListPage = true;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (useHoppVerticalEventListPage) {
        return {
            useInternalRouting: true
        };
    }

    // Fall back to external URL
    if (htmlSiteInfo) {
        const externalUrl = doppeHtmlSiteResolveEventsPageURL(htmlSiteInfo, viewerLang, showMobileView);
        return {
            useInternalRouting: false,
            externalUrl: externalUrl
        };
    }

    return {
        useInternalRouting: false,
        externalUrl: null
    };
}

// ============================================================================
// Internal Page Path Resolution (subtype-aware)
// ============================================================================

/**
 * Resolves the correct internal page path for a product, accounting for digital products.
 * 
 * - Digital products → /digital-product/:slug
 * - Physical/other products → /product/:slug
 */
export function viewerEntityNavigationResolveProductInternalPagePath(
    routesManager: DoppeViewerPagesRoutesManager,
    slug: string,
    productType?: DoppeDtoStoreProductType
): string {
    if (productType === DoppeDtoStoreProductType.Digital) {
        return routesManager.digitalProductItemPage({productSlug: slug});
    }
    return routesManager.productItemPage({productSlug: slug});
}

/**
 * Resolves the correct internal page path for a blog post.
 * 
 * - Blog posts → /post/:slug
 */
export function viewerEntityNavigationResolveBlogPostInternalPagePath(
    routesManager: DoppeViewerPagesRoutesManager,
    slug: string,
): string {
    return routesManager.blogPostItemPage({postSlug: slug});
}

/**
 * Resolves the correct internal page path for a service, accounting for service types.
 * 
 * - Appointments → /appointment/:slug
 * - Classes → /class/:slug
 * - Courses → /course/:slug
 * - Unknown/other → /service/:slug
 */
export function viewerEntityNavigationResolveServiceInternalPagePath(
    routesManager: DoppeViewerPagesRoutesManager,
    slug: string,
    serviceType?: DoppeDtoBookingServiceType
): string {
    switch (serviceType) {
        case DoppeDtoBookingServiceType.APPOINTMENT:
            return routesManager.appointmentItemPage({serviceSlug: slug});
        case DoppeDtoBookingServiceType.Class:
            return routesManager.classItemPage({serviceSlug: slug});
        case DoppeDtoBookingServiceType.COURSE:
            return routesManager.courseItemPage({serviceSlug: slug});
        default:
            return routesManager.serviceItemPage({serviceSlug: slug});
    }
}

/**
 * Resolves the correct internal page path for an event, accounting for ticketing events.
 * 
 * - Ticketing events → /ticket-event/:slug
 * - RSVP/other events → /event/:slug
 */
export function viewerEntityNavigationResolveEventInternalPagePath(
    routesManager: DoppeViewerPagesRoutesManager,
    slug: string,
    registrationType?: DoppeDtoEventRegistrationType
): string {
    if (registrationType === DoppeDtoEventRegistrationType.Ticketing) {
        return routesManager.ticketingEventItemPage({eventSlug: slug});
    }
    return routesManager.eventItemPage({eventSlug: slug});
}

// ============================================================================
// Entity Open Action: Booking & Registration Target Resolution
// ============================================================================

/**
 * Resolves the booking target URL for a service entity.
 * For APPOINTMENT/CLASS: uses the calendar page URL.
 * For COURSE: uses the booking form URL with query parameters.
 * Falls back to the service details page if booking URL cannot be resolved.
 */
export function viewerEntityNavigationResolveServiceBookingTarget(
    entityInfo: VerticalEntityInfo & {
        servicePageUrl?: string | null;
        serviceType?: DoppeDtoBookingServiceType;
        calendarPageUrl?: string | null;
        serviceId?: string;
    },
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (entityInfo.serviceType === DoppeDtoBookingServiceType.COURSE) {
        if (htmlSiteInfo && entityInfo.serviceId) {
            const bookingFormUrl = doppeHtmlSiteResolveBookingFormUrl({
                doppeHtmlSiteInfo: htmlSiteInfo,
                serviceId: entityInfo.serviceId,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                viewerLang: viewerLang,
                showMobileView: showMobileView
            });
            if (bookingFormUrl) {
                return {
                    useInternalRouting: false,
                    externalUrl: bookingFormUrl
                };
            }
        }
    } else {
        const calendarPageUrl = entityInfo.calendarPageUrl;
        if (htmlSiteInfo && calendarPageUrl) {
            const externalUrl = doppeHtmlSiteResolveServicePageUrl({
                doppeHtmlSiteInfo: htmlSiteInfo,
                pageURL: calendarPageUrl,
                viewerLang: viewerLang,
                showMobileView: showMobileView
            });
            if (externalUrl) {
                return {
                    useInternalRouting: false,
                    externalUrl: externalUrl
                };
            }
        }
    }

    return viewerEntityNavigationResolveServiceTarget(entityInfo, options);
}

/**
 * Resolves the registration target URL for an event entity.
 * For RSVP events: uses the RSVP form URL.
 * For Ticketed events: returns the event details page (viewer-side modal handles ticket flow).
 * Falls back to the event details page if registration URL cannot be resolved.
 */
export function viewerEntityNavigationResolveEventRegistrationTarget(
    entityInfo: VerticalEntityInfo & {
        registrationType?: DoppeDtoEventRegistrationType;
    },
    options: VerticalEntityNavigationOptions
): VerticalEntityNavigationResult {
    const {bymoPageContextProps, viewerLang, showMobileView} = options;
    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    if (entityInfo.registrationType === DoppeDtoEventRegistrationType.RSVP) {
        if (htmlSiteInfo && entityInfo.slug) {
            const rsvpFormUrl = doppeHtmlSiteResolveEventRsvpFormUrl({
                doppeHtmlSiteInfo: htmlSiteInfo,
                slug: entityInfo.slug,
                viewerLang: viewerLang,
                showMobileView: showMobileView
            });
            if (rsvpFormUrl) {
                return {
                    useInternalRouting: false,
                    externalUrl: rsvpFormUrl
                };
            }
        }
    }

    return viewerEntityNavigationResolveEventTarget(entityInfo, options);
}

